import "./inputs.scss";
import * as React from "react";
import { type CSSProperties, type HTMLProps } from "react";
import { getCurrencySymbol, isCurrencySymbolBeforeNumbers } from "../../Utilities/Generic/Numbers";

interface Props extends HTMLProps<HTMLInputElement> {
    inputRef?: React.RefObject<HTMLInputElement | null> | React.RefCallback<HTMLInputElement>;
}

const ALLOWED_KEYS = [ ".", "Backspace", "Delete", "ArrowLeft", "ArrowRight", "-", "Tab" ];

export default class CurrencyInput extends React.Component<Props> {
    protected handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        // This is implemented due to this React issue: https://github.com/facebook/react/issues/6556
        if(typeof this.props.onKeyDown === "function") {
            this.props.onKeyDown(e);
        }

        if(e.isDefaultPrevented()) {
            return;
        }

        const modifier = e.altKey || e.ctrlKey || e.metaKey;

        if(
            !modifier &&
            (
                !(
                    ALLOWED_KEYS.includes(e.key) ||
                    /[0-9]+/g.test(e.key)
                ) ||
                (
                    e.key === "." &&
                    ("" + this.props.value).indexOf(".") !== -1
                )
            )
        ) {
            e.preventDefault();
        }
    };

    protected handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
        if(typeof this.props.onPaste === "function") {
            this.props.onPaste(e);
        }

        if(e.isDefaultPrevented()) {
            return;
        }

        e.preventDefault();

        const data = parseFloat(e.clipboardData.getData("text").replace(",", ""));

        if(isNaN(data)) {
            return;
        }

        // Adapted from this commit on cypress-io/cypress
        // https://github.com/cypress-io/cypress/commit/4a56ca83b3b6e19ec57d10a8160f54f513e8f8ec
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;

        if(nativeInputValueSetter) {
            nativeInputValueSetter.call(e.currentTarget, data.toString());
        } else {
            e.currentTarget.value = data.toString();
        }

        e.currentTarget.dispatchEvent(new Event("input", {
            bubbles: true,
        }));
    };

    public render(): React.ReactNode {
        const { className, inputRef, ...forwardProps } = this.props;

        const classes = [ "currency-input" ];
        const wrapperClasses = [ "currency-input-wrapper" ];

        if(className) {
            classes.push(className);
        }

        if(isCurrencySymbolBeforeNumbers()) {
            wrapperClasses.push("currency-before");
        } else {
            wrapperClasses.push("currency-after");
        }

        const symbol = getCurrencySymbol();

        return (
            <div
                className={wrapperClasses.join(" ")}
                style={{
                    "--currency-symbol"      : `"${symbol}"`,
                    "--currency-symbol-width": `${symbol.length}ch`,
                } as CSSProperties}
            >
                <input
                    {...forwardProps}
                    className={classes.join(" ")}
                    type="text"
                    onKeyDown={this.handleKeyDown}
                    ref={inputRef}
                    onPaste={this.handlePaste}
                />
            </div>
        );
    }
}
